const createState = () => ({
  moving: false,
  movingIndex: 0,
  startX: 0,
  startWidth: 0,
  widths: new Map(),
  resizeContainer: null,
  bars: [],
  ths: [],
  table: null,
  thead: null,
  wrapper: null,
  mutationObserver: null,
  resizeObserver: null,
  rebuildScheduled: false,
})

const resolveTable = (el) => {
  if (el.tagName === 'TABLE') return el
  if (el.tagName === 'THEAD') return el.closest('table')
  return el.querySelector('table')
}

const resolveWrapper = (table) => table.closest('.v-table__wrapper') || table.parentElement

const setColumnWidth = (th, width) => {
  th.style.width = width + 'px'
  th.style.minWidth = width + 'px'
}

const getColumnWidth = (th) => {
  const value = th.style.width
  if (value && value.endsWith('px')) return Number(value.replace('px', ''))
  return th.getBoundingClientRect().width
}

const ensureContainer = (state) => {
  if (state.resizeContainer) {
    state.resizeContainer.remove()
    state.resizeContainer = null
  }

  const { table, wrapper, thead } = state
  if (!table || !wrapper) return

  if (getComputedStyle(wrapper).position === 'static') {
    wrapper.style.position = 'relative'
  }

  const resizeContainer = document.createElement('div')
  resizeContainer.className = 'vue-columns-resizable'
  resizeContainer.style.position = 'absolute'
  resizeContainer.style.left = 0
  resizeContainer.style.top = table.offsetTop + 'px'
  resizeContainer.style.width = table.offsetWidth + 'px'
  resizeContainer.style.height = (thead ? thead.offsetHeight : table.offsetHeight) + 'px'
  resizeContainer.style.pointerEvents = 'none'

  wrapper.appendChild(resizeContainer)
  state.resizeContainer = resizeContainer
}

const updateContainerSize = (state) => {
  const { table, thead, resizeContainer } = state
  if (!table || !resizeContainer) return
  resizeContainer.style.top = table.offsetTop + 'px'
  resizeContainer.style.width = table.offsetWidth + 'px'
  resizeContainer.style.height = (thead ? thead.offsetHeight : table.offsetHeight) + 'px'
}

const startDrag = (state, index, clientX) => {
  state.moving = true
  state.movingIndex = index
  state.startX = clientX
  state.startWidth = getColumnWidth(state.ths[index])
  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'
}

const createBar = (state, index) => {
  const nextTh = state.ths[index + 1]
  const bar = document.createElement('div')
  const left = state.table.offsetLeft + nextTh.offsetLeft - 4

  bar.style.position = 'absolute'
  bar.style.left = left + 'px'
  bar.style.top = 0
  bar.style.height = (state.thead ? state.thead.offsetHeight : state.table.offsetHeight) + 'px'
  bar.style.width = '8px'
  bar.style.cursor = 'col-resize'
  bar.style.zIndex = 2
  bar.style.pointerEvents = 'auto'
  bar.className = 'columns-resize-bar'

  bar.addEventListener('mousedown', (e) => startDrag(state, index, e.clientX))

  return bar
}

const renderBars = (state) => {
  state.resizeContainer.innerHTML = ''
  state.bars = []

  state.ths.forEach((th, index) => {
    const width = state.widths.get(index) || getColumnWidth(th)
    setColumnWidth(th, width)

    if (index + 1 >= state.ths.length) return

    const bar = createBar(state, index)
    state.resizeContainer.appendChild(bar)
    state.bars.push(bar)
  })
}

const updateBars = (state) => {
  if (!state.table || !state.thead) return
  state.bars.forEach((bar, index) => {
    const nextTh = state.ths[index + 1]
    if (!nextTh) return
    bar.style.left = state.table.offsetLeft + nextTh.offsetLeft - 4 + 'px'
    bar.style.height = state.thead.offsetHeight + 'px'
  })
  updateContainerSize(state)
}

const observeMutations = (state, el) => {
  const observer = new MutationObserver(() => state.onRebuild())
  const target = state.thead || state.table || el
  const options = state.thead || state.table
    ? { childList: true, subtree: true, attributes: !!state.thead }
    : { childList: true, subtree: true }
  observer.observe(target, options)
  return observer
}

const observeResize = (state) => {
  const observer = new ResizeObserver(() => updateBars(state))
  if (state.table) observer.observe(state.table)
  if (state.wrapper) observer.observe(state.wrapper)
  return observer
}

export const createResizer = (el) => {
  const state = createState()

  const scheduleRebuild = () => {
    if (state.rebuildScheduled) return
    state.rebuildScheduled = true
    requestAnimationFrame(() => {
      state.rebuildScheduled = false
      rebuild()
    })
  }

  const rebuild = () => {
    state.table = resolveTable(el)
    if (!state.table) return scheduleRebuild()

    state.wrapper = resolveWrapper(state.table)
    state.thead = state.table.querySelector('thead')
    if (!state.thead) return scheduleRebuild()

    state.ths = Array.from(state.thead.querySelectorAll('th'))
    if (!state.ths.length) return scheduleRebuild()

    ensureContainer(state)
    updateContainerSize(state)
    renderBars(state)
  }

  state.onRebuild = rebuild

  const handleMouseMove = (e) => {
    if (!state.moving) return
    const th = state.ths[state.movingIndex]
    if (!th) return
    const delta = e.clientX - state.startX
    const nextWidth = Math.max(40, state.startWidth + delta)
    setColumnWidth(th, nextWidth)
    state.widths.set(state.movingIndex, nextWidth)
    updateBars(state)
  }

  const handleMouseUp = () => {
    if (!state.moving) return
    state.moving = false
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
    updateBars(state)
  }

  const handleScroll = () => updateBars(state)

  rebuild()
  state.mutationObserver = observeMutations(state, el)
  state.resizeObserver = observeResize(state)

  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
  if (state.wrapper) state.wrapper.addEventListener('scroll', handleScroll)

  return () => {
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
    if (state.wrapper) state.wrapper.removeEventListener('scroll', handleScroll)
    if (state.mutationObserver) state.mutationObserver.disconnect()
    if (state.resizeObserver) state.resizeObserver.disconnect()
    if (state.resizeContainer) state.resizeContainer.remove()
  }
}

export default {
  /** @param {import('vue').App} app */
  install (app) {
    app.directive('resizable-columns', {
      mounted (el) {
        el.__columnsResizableCleanup__ = createResizer(el)
      },
      unmounted (el) {
        if (el.__columnsResizableCleanup__) {
          el.__columnsResizableCleanup__()
          delete el.__columnsResizableCleanup__
        }
      },
    })
  },
}
